import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users, Sparkles, TrendingUp, Gift, Settings as SettingsIcon, BarChart3,
  ChevronLeft, Loader2, Save, LogOut, Share2,
} from "lucide-react";
import { toast } from "sonner";
import { adminApi, paymentsApi } from "../lib/api";
import { StripeAdminCard } from "../components/StripeAdminCard";
import { AiCacheCard } from "../components/AiCacheCard";
import { FoinnLogo } from "../components/FoinnLogo";
import { useAuth } from "../contexts/AuthContext";

const Stat = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="rounded-2xl border border-[#e5e0d8] bg-white p-4" data-testid={testid}>
    <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[#a89e92]">
      <Icon className="h-4 w-4" /> {label}
    </div>
    <div className="mt-1 text-2xl font-black text-[#2d2824]">{value}</div>
    {sub && <div className="text-xs font-semibold text-[#6e655b]">{sub}</div>}
  </div>
);

const SettingField = ({ label, value, step = 1, onChange, testid }) => (
  <label className="mb-2 block text-sm font-semibold text-[#6e655b]">
    {label}
    <input
      type="number"
      step={step}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1 w-full rounded-xl border border-[#e5e0d8] px-3 py-2 text-[#2d2824]"
      data-testid={testid}
    />
  </label>
);

const FameChart = ({ dist }) => {
  const max = Math.max(...dist.bands.map((b) => b.count), 1);
  return (
    <div className="flex h-48 items-end gap-1.5" data-testid="fame-chart">
      {dist.bands.map((b) => (
        <div key={b.band} className="flex flex-1 flex-col items-center justify-end gap-1">
          <span className="text-[10px] font-bold text-[#6e655b]">{b.count}</span>
          <div
            className="w-full rounded-t-md transition-all"
            style={{
              height: `${Math.max(3, (b.count / max) * 100)}%`,
              background: `hsl(${140 - (b.band / 100) * 140} 70% ${60 - (b.band / 100) * 15}%)`,
            }}
            title={`${b.band}–${b.band + 10}%: ${b.count} tunes`}
          />
          <span className="text-[10px] font-bold text-[#a89e92]">{b.band}</span>
        </div>
      ))}
    </div>
  );
};

export default function AdminPage() {
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [txns, setTxns] = useState([]);
  const [settings, setSettings] = useState(null);
  const [dist, setDist] = useState(null);
  const [saving, setSaving] = useState(false);
  const [giftEmail, setGiftEmail] = useState("");
  const [giftPlan, setGiftPlan] = useState("monthly");
  const [giftCalls, setGiftCalls] = useState(10);

  const refreshAll = () => {
    adminApi.stats().then(setStats).catch(() => {});
    adminApi.users().then((r) => setUsers(r.users || [])).catch(() => {});
    adminApi.transactions().then((r) => setTxns(r.transactions || [])).catch(() => {});
    adminApi.getSettings().then(setSettings).catch(() => {});
    adminApi.fameDistribution().then(setDist).catch(() => {});
  };

  useEffect(() => {
    if (user?.is_admin) refreshAll();
  }, [user]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#2d2824]" />
      </div>
    );
  }

  if (!user?.is_admin) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 text-center">
        <h1 className="font-serif text-3xl font-bold text-[#2d2824]">Admins only</h1>
        <button onClick={() => navigate("/")} className="rounded-full bg-[#2d2824] px-6 py-3 font-bold text-white">
          Back to Foinn.org
        </button>
      </div>
    );
  }

  const saveSettings = async () => {
    setSaving(true);
    try {
      const s = await adminApi.updateSettings({
        welcome_calls: Number(settings.welcome_calls),
        guest_daily_searches: Number(settings.guest_daily_searches),
        free_daily_searches: Number(settings.free_daily_searches),
        monthly_calls: Number(settings.monthly_calls),
        annual_calls: Number(settings.annual_calls),
        usd_rate: Number(settings.usd_rate),
      });
      setSettings({ ...settings, ...s });
      toast.success("Settings saved");
    } catch {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  const gift = async () => {
    if (!giftEmail.trim()) return;
    try {
      const r = await paymentsApi.gift(giftEmail.trim(), giftPlan, Number(giftCalls) || 0);
      toast.success(`Gifted to ${r.gifted.email}`);
      setGiftEmail("");
      refreshAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not gift");
    }
  };

  return (
    <div className="min-h-screen bg-[#f7f3ec] px-4 py-6 sm:px-8" data-testid="admin-page">
      <header className="mx-auto mb-6 flex max-w-6xl items-center gap-3">
        <button
          onClick={() => navigate("/")}
          className="rounded-full border border-[#e5e0d8] bg-white p-2 text-[#2d2824] hover:bg-[#f3efea]"
          data-testid="admin-back"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <FoinnLogo className="h-10 w-10" />
        <h1 className="font-serif text-3xl font-bold text-[#2d2824]">Admin dashboard</h1>
        <button
          onClick={logout}
          className="ml-auto flex items-center gap-2 rounded-full border border-[#e5e0d8] bg-white px-4 py-2 text-sm font-bold text-[#6e655b] hover:bg-[#f3efea] hover:text-[#e11d48]"
          data-testid="admin-signout"
        >
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </header>

      <div className="mx-auto max-w-6xl space-y-6">
        {stats && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat
              icon={Users}
              label="Users"
              value={stats.users_total}
              sub={`${stats.premium_users} premium`}
              testid="stat-users"
            />
            <Stat
              icon={TrendingUp}
              label="Revenue"
              value={`€${stats.revenue.total.toFixed(2)}`}
              sub={`€${stats.revenue.subscriptions.toFixed(2)} subs · €${stats.revenue.donations.toFixed(2)} tips`}
              testid="stat-revenue"
            />
            <Stat
              icon={Sparkles}
              label="AI analyses"
              value={stats.ai_analyses}
              sub={`${stats.ai_cached} cached · ${stats.ai_prepopulated} pre-built`}
              testid="stat-ai"
            />
            <Stat
              icon={Sparkles}
              label="Analyses owed"
              value={stats.calls_outstanding}
              sub="unspent user allowances"
              testid="stat-credits"
            />
          </div>
        )}

        {dist && (
          <div className="rounded-2xl border border-[#e5e0d8] bg-white p-5">
            <div className="mb-3 flex items-center gap-2 font-bold text-[#2d2824]">
              <BarChart3 className="h-5 w-5" /> Tunes per fame band ({dist.total.toLocaleString()} tunes)
            </div>
            <FameChart dist={dist} />
            <p className="mt-2 text-xs font-semibold text-[#6e655b]">
              Rare-fame ranking (0 = one-off submissions, 90+ = session standards).
            </p>
          </div>
        )}

        <div className="grid gap-6 lg:grid-cols-2">
          <StripeAdminCard />
          <AiCacheCard />
          <div className="rounded-2xl border border-[#e5e0d8] bg-white p-4" data-testid="social-studio-card">
            <h3 className="font-serif text-xl font-bold text-[#2d2824]">Generate for social</h3>
            <p className="mt-1 text-xs font-semibold text-[#6e655b]">
              Cover packs, tune posts and feature carousels — Foinn.org-styled PNGs zipped with an
              AI-written caption.
            </p>
            <button
              onClick={() => navigate("/admin/social")}
              className="mt-3 flex items-center gap-2 rounded-full bg-[#8a2be2] px-5 py-2.5 text-sm font-extrabold text-white transition-transform hover:scale-105"
              data-testid="open-social-studio"
            >
              <Share2 className="h-4 w-4" /> Open the social studio
            </button>
          </div>
          {settings && (
            <div className="rounded-2xl border border-[#e5e0d8] bg-white p-5">
              <div className="mb-3 flex items-center gap-2 font-bold text-[#2d2824]">
                <SettingsIcon className="h-5 w-5" /> Plans & limits
              </div>
              <SettingField
                label="Welcome AI analyses (new accounts)"
                value={settings.welcome_calls}
                onChange={(v) => setSettings({ ...settings, welcome_calls: v })}
                testid="setting-welcome"
              />
              <SettingField
                label="Guest searches / day"
                value={settings.guest_daily_searches}
                onChange={(v) => setSettings({ ...settings, guest_daily_searches: v })}
                testid="setting-guest-searches"
              />
              <SettingField
                label="Signed-in searches / day"
                value={settings.free_daily_searches}
                onChange={(v) => setSettings({ ...settings, free_daily_searches: v })}
                testid="setting-free-searches"
              />
              <SettingField
                label="Monthly plan · analyses / month"
                value={settings.monthly_calls}
                onChange={(v) => setSettings({ ...settings, monthly_calls: v })}
                testid="setting-monthly-calls"
              />
              <SettingField
                label="Annual plan · analyses / month"
                value={settings.annual_calls}
                onChange={(v) => setSettings({ ...settings, annual_calls: v })}
                testid="setting-annual-calls"
              />
              <button
                onClick={saveSettings}
                disabled={saving}
                className="flex items-center gap-2 rounded-full bg-[#2d2824] px-5 py-2.5 font-bold text-white disabled:opacity-60"
                data-testid="setting-save"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
              </button>

              <div className="mt-4 rounded-xl bg-[#f7f3ec] p-3 text-sm">
                <div className="font-bold text-[#2d2824]">Prices shown in dollars</div>
                <div className="text-[#6e655b]">
                  EUR → USD rate used next to euro prices.
                </div>
                <SettingField
                  label="usd_rate"
                  step={0.01}
                  value={settings.usd_rate}
                  onChange={(v) => setSettings({ ...settings, usd_rate: v })}
                  testid="setting-usd-rate"
                />
              </div>
            </div>
          )}

          <div className="rounded-2xl border border-[#e5e0d8] bg-white p-5">
            <div className="mb-3 flex items-center gap-2 font-bold text-[#2d2824]">
              <Gift className="h-5 w-5" /> Gift Premium or analyses
            </div>
            <p className="mb-3 text-sm text-[#6e655b]">
              The person must have signed in at least once. A gifted plan gives unlimited searches
              and its monthly allowance.
            </p>
            <input
              value={giftEmail}
              onChange={(e) => setGiftEmail(e.target.value)}
              placeholder="user@email.com"
              className="mb-2 w-full rounded-xl border border-[#e5e0d8] px-3 py-2"
              data-testid="admin-gift-email"
            />
            <div className="flex items-center gap-2">
              <select
                value={giftPlan}
                onChange={(e) => setGiftPlan(e.target.value)}
                className="rounded-xl border border-[#e5e0d8] px-2 py-2 text-sm"
                data-testid="admin-gift-plan"
              >
                <option value="monthly">Monthly plan</option>
                <option value="annual">Annual plan</option>
                <option value="none">Analyses only</option>
              </select>
              <input
                type="number"
                value={giftCalls}
                onChange={(e) => setGiftCalls(e.target.value)}
                className="w-20 rounded-xl border border-[#e5e0d8] px-3 py-2"
                data-testid="admin-gift-credit"
              />
              <button
                onClick={gift}
                className="flex-1 rounded-xl bg-[#2d2824] px-4 py-2 font-bold text-white"
                data-testid="admin-gift-submit"
              >
                Gift
              </button>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-[#e5e0d8] bg-white p-5">
          <div className="mb-3 font-bold text-[#2d2824]">Users ({users.length})</div>
          <div className="max-h-80 overflow-auto">
            <table className="w-full text-left text-sm" data-testid="admin-users-table">
              <thead className="sticky top-0 bg-white text-xs uppercase text-[#a89e92]">
                <tr>
                  <th className="py-2">Email</th><th>Name</th><th>Plan</th><th>Analyses</th>
                  <th>Expires</th><th>Admin</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.user_id} className="border-t border-[#f3efea]">
                    <td className="py-2 pr-2 font-semibold text-[#2d2824]">{u.email}</td>
                    <td className="pr-2 text-[#6e655b]">{u.name}</td>
                    <td className="pr-2 capitalize">{u.plan}</td>
                    <td className="pr-2">{u.calls_left}</td>
                    <td className="pr-2 text-[#6e655b]">
                      {u.plan_expires_at ? new Date(u.plan_expires_at).toLocaleDateString() : "—"}
                    </td>
                    <td>{u.is_admin ? "✓" : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rounded-2xl border border-[#e5e0d8] bg-white p-5">
          <div className="mb-3 font-bold text-[#2d2824]">Recent payments ({txns.length})</div>
          {txns.length === 0 ? (
            <p className="text-sm text-[#6e655b]">No paid transactions yet.</p>
          ) : (
            <div className="max-h-72 overflow-auto">
              <table className="w-full text-left text-sm" data-testid="admin-txns-table">
                <thead className="sticky top-0 bg-white text-xs uppercase text-[#a89e92]">
                  <tr><th className="py-2">Item</th><th>Amount</th><th>User</th><th>Date</th></tr>
                </thead>
                <tbody>
                  {txns.map((t, i) => (
                    <tr key={i} className="border-t border-[#f3efea]">
                      <td className="py-2 pr-2 font-semibold text-[#2d2824]">{t.lookup_key}</td>
                      <td className="pr-2">€{Number(t.amount).toFixed(2)}</td>
                      <td className="pr-2 text-[#6e655b]">{t.user_id}</td>
                      <td className="text-[#6e655b]">
                        {t.created_at ? new Date(t.created_at).toLocaleString() : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
